import { useState, useCallback } from 'react';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Button,
  Typography,
  Link,
  Alert,
  InputAdornment,
  IconButton,
  CircularProgress,
  Divider,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useAuth } from '../../lib/AuthContext';
import { GoogleSignInButton } from '../../components/auth';
import { trpc } from '../../lib/trpc';

// Check if Google OAuth is configured
const GOOGLE_CLIENT_ID = import.meta.env.VITE_GOOGLE_CLIENT_ID;

/**
 * Validate email format
 */
function isValidEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

interface FormErrors {
  email?: string;
  password?: string;
}

interface LocationState {
  from?: { pathname: string };
}

/**
 * Login page component
 * Handles user authentication with proper validation and error handling
 */
export function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formErrors, setFormErrors] = useState<FormErrors>({});
  const [isLoading, setIsLoading] = useState(false);
  const [touched, setTouched] = useState<{ email: boolean; password: boolean }>({
    email: false,
    password: false,
  });

  // Get the redirect destination from location state
  const from = (location.state as LocationState)?.from?.pathname || '/monitors';

  // Check if Google OAuth is available on the backend
  const { data: googleOAuthStatus } = trpc.auth.isGoogleOAuthAvailable.useQuery(undefined, {
    // Only check if we have a client ID configured
    enabled: !!GOOGLE_CLIENT_ID,
  });
  const isGoogleOAuthAvailable = !!GOOGLE_CLIENT_ID && googleOAuthStatus?.available;

  /**
   * Handle successful Google sign-in
   */
  const handleGoogleSuccess = () => {
    navigate(from, { replace: true });
  };

  /**
   * Handle Google sign-in error
   */
  const handleGoogleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  /**
   * Validate form fields
   */
  const validateForm = useCallback((): FormErrors => {
    const errors: FormErrors = {};

    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!isValidEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!password) {
      errors.password = 'Password is required';
    }

    return errors;
  }, [email, password]);

  /**
   * Handle field blur for validation
   */
  const handleBlur = (field: 'email' | 'password') => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    const errors = validateForm();
    setFormErrors(errors);
  };

  /**
   * Handle form submission
   */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    // Mark all fields as touched
    setTouched({ email: true, password: true });

    // Validate form
    const errors = validateForm();
    setFormErrors(errors);

    // Check if there are any errors
    if (Object.keys(errors).length > 0) {
      return;
    }

    // Submit login request using useAuth hook
    setIsLoading(true);
    try {
      await login(email.trim().toLowerCase(), password);
      // Redirect to the page they were trying to visit, or monitors page
      navigate(from, { replace: true });
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to login. Please check your credentials.'
      );
    } finally {
      setIsLoading(false);
    }
  };

  /**
   * Toggle password visibility
   */
  const handleTogglePassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      <Typography variant="h5" component="h2" sx={{ fontWeight: 600, mb: 1 }}>
        Welcome back
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Sign in to your account to continue
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }} role="alert">
          {error}
        </Alert>
      )}

      <TextField
        fullWidth
        id="email"
        name="email"
        label="Email"
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        onBlur={() => handleBlur('email')}
        error={touched.email && !!formErrors.email}
        helperText={touched.email && formErrors.email}
        required
        autoComplete="email"
        autoFocus
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Email address',
          'aria-describedby': formErrors.email ? 'email-error' : undefined,
        }}
        sx={{ mb: 2 }}
      />

      <TextField
        fullWidth
        id="password"
        name="password"
        label="Password"
        type={showPassword ? 'text' : 'password'}
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        onBlur={() => handleBlur('password')}
        error={touched.password && !!formErrors.password}
        helperText={touched.password && formErrors.password}
        required
        autoComplete="current-password"
        disabled={isLoading}
        inputProps={{
          'aria-label': 'Password',
          'aria-describedby': formErrors.password ? 'password-error' : undefined,
        }}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                onClick={handleTogglePassword}
                edge="end"
                disabled={isLoading}
              >
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          ),
        }}
        sx={{ mb: 1 }}
      />

      <Box sx={{ textAlign: 'right', mb: 3 }}>
        <Link
          component={RouterLink}
          to="/forgot-password"
          variant="body2"
          sx={{ textDecoration: 'none' }}
          tabIndex={isLoading ? -1 : 0}
        >
          Forgot password?
        </Link>
      </Box>

      <Button
        type="submit"
        fullWidth
        variant="contained"
        size="large"
        disabled={isLoading}
        aria-label={isLoading ? 'Signing in' : 'Sign in'}
        sx={{ mb: 2, height: 48 }}
      >
        {isLoading ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={20} color="inherit" />
            <span>Signing in...</span>
          </Box>
        ) : (
          'Sign in'
        )}
      </Button>

      {isGoogleOAuthAvailable && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              or
            </Typography>
          </Divider>

          <GoogleSignInButton
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
            disabled={isLoading}
          />
        </>
      )}

      <Box sx={{ textAlign: 'center', mt: 2 }}>
        <Typography variant="body2" color="text.secondary">
          Don&apos;t have an account?{' '}
          <Link
            component={RouterLink}
            to="/register"
            sx={{ textDecoration: 'none' }}
            tabIndex={isLoading ? -1 : 0}
          >
            Sign up
          </Link>
        </Typography>
      </Box>
    </Box>
  );
}

export default LoginPage;
